import React from "react";
import { Link } from "react-router-dom";
import { FaGamepad, FaLock, FaNetworkWired, FaUser, FaUsers } from "react-icons/fa";
import "./GameRoom.css";
import GameRoomScoreboard from "./games/shared/GameRoomScoreboard";

const games = [
  {
    key: "blackjack",
    title: "Blackjack",
    tagline: "Beat the dealer",
    description:
      "Casino-style blackjack with a real shoe, splitting, doubling, surrender, dealer logic, and session stats.",
    traits: "Skill · Strategy · Luck",
    to: "/games/blackjack",
    image: "/images/games/blackjack-box.png",
    players: "1–4 Players",
    multiplayer: "Online Live",
    onlineTo: "/games/blackjack/online",
  },
  {
    key: "chess",
    title: "Chess",
    tagline: "A timeless classic",
    description:
      "Full chess with legal move validation, AI and local play, castling, en passant, promotion, draws, and move history.",
    traits: "Strategy · Patience · Victory",
    to: "/games/chess",
    image: "/images/games/chess-box.png",
    players: "1–2 Players",
    multiplayer: "Online Beta",
    onlineTo: "/games/chess/online",
  },
  {
    key: "minesweeper",
    title: "Minesweeper",
    tagline: "Clear minds win",
    description:
      "The classic logic game rebuilt with modern controls, multiple difficulties, stats, and Hobbli polish.",
    traits: "Think · Uncover · Win",
    to: "/games/minesweeper",
    image: "/images/games/minesweeper-box.png",
    players: "1 Player",
  },
  {
    key: "block-drop",
    title: "Block Drop",
    tagline: "Stack higher",
    description:
      "Stack falling blocks, clear lines, build combos, and survive as the speed climbs, with hold, preview, ghost placement, and mobile controls.",
    traits: "Simple · Addictive · Endless",
    to: "/games/block-drop",
    image: "/images/games/block-drop-box.png",
    players: "1 Player",
  },
  {
    key: "meteor-smash",
    title: "Meteor Smash",
    tagline: "Defend · Shoot · Survive",
    description:
      "Blast falling meteors and bombs, collect power-ups, protect the surface, and survive increasingly difficult waves.",
    traits: "Bigger Waves · Higher Scores",
    to: "/games/meteor-smash",
    image: "/images/games/meteor-smash-box.png",
    players: "1 Player",
  },
  {
    key: "pinball",
    title: "Orbital Vault",
    tagline: "Light · Lock · Launch",
    description:
      "A Hobbli pinball table currently being tuned and polished. Missions, multiball, jackpots, and more are coming soon.",
    traits: "Pinball · Missions · High Scores",
    image: "/images/games/pinball-box.png",
    players: "1 Player",
    comingSoon: true,
  },
];

export default function GameRoom({ currentUser }) {
  return (
    <div className="hobbli-game-room hobbli-game-room--arcade">
      <section className="game-room-arcade-shell">
        <header className="game-room-arcade-header">
          <div className="game-room-arcade-brand">
            <span className="game-room-arcade-controller" aria-hidden="true">
              <FaGamepad />
            </span>
            <div>
              <strong>HOBBLI</strong>
              <span>GAME ROOM</span>
            </div>
          </div>

          <p>
            GOOD GAMES.<br />
            GOOD PEOPLE.<br />
            SAME UNIVERSE.
          </p>
        </header>

        <div className="game-room-racing-stripes" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>

        <div className="game-room-arcade-motto">
          <span>PLAY</span><b>•</b><span>RELAX</span><b>•</b><span>COMPETE</span><b>•</b><span>BELONG</span>
        </div>

        <GameRoomScoreboard currentUser={currentUser} />

        <section className="game-room-library" aria-label="Hobbli games">
          {games.map((game) => (
            <article
              className={`game-room-cartridge-card ${
                game.comingSoon ? "game-room-cartridge-card--coming-soon" : ""
              }`}
              key={game.key}
            >
              {game.comingSoon ? (
                <div
                  className="game-room-cartridge-link game-room-cartridge-link--disabled"
                  aria-label={`${game.title} coming soon`}
                >
                  <span className="game-room-cartridge-image-wrap">
                    <img
                      src={game.image}
                      alt={`${game.title} Hobbli Game Room box art`}
                      loading="lazy"
                    />
                    <span
                      className="game-room-cartridge-glare"
                      aria-hidden="true"
                    />

                    <span className="game-room-coming-soon-overlay">
                      <FaLock />
                      Coming Soon
                    </span>
                  </span>
                </div>
              ) : (
                <Link
                  to={game.to}
                  className="game-room-cartridge-link"
                  aria-label={`Play ${game.title}`}
                >
                  <span className="game-room-cartridge-image-wrap">
                    <img
                      src={game.image}
                      alt={`${game.title} Hobbli Game Room box art`}
                      loading="lazy"
                    />
                    <span
                      className="game-room-cartridge-glare"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              )}

              <div className="game-room-cartridge-info">
                <span className="game-room-cartridge-tagline">
                  {game.tagline}
                </span>

                <h2>{game.title}</h2>

                <p>{game.description}</p>

                <div
                  className="game-room-game-badges"
                  aria-label={`${game.title} player information`}
                >
                  <span className="game-room-player-badge">
                    {game.players === "1 Player" ? <FaUser /> : <FaUsers />}
                    {game.players}
                  </span>

                  {game.multiplayer && (
                    <span className="game-room-online-badge">
                      <FaNetworkWired /> {game.multiplayer}
                    </span>
                  )}

                  {game.comingSoon && (
                    <span className="game-room-online-badge">
                      <FaLock /> Coming Soon
                    </span>
                  )}
                </div>

                <small>{game.traits}</small>

                <div className="game-room-play-actions">
                  {game.comingSoon ? (
                    <button
                      type="button"
                      className="game-room-arcade-play game-room-arcade-play--disabled"
                      disabled
                    >
                      <FaLock /> Coming Soon
                    </button>
                  ) : (
                    <>
                      <Link to={game.to} className="game-room-arcade-play">
                        {game.onlineTo ? "Solo / Local" : "Play Now"}
                      </Link>

                      {game.onlineTo && (
                        <Link
                          to={game.onlineTo}
                          className="game-room-arcade-play game-room-arcade-play--online"
                        >
                          Play Online
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </div>
            </article>
          ))}
        </section>

        <section className="game-room-multiplayer-preview">
          <div className="game-room-multiplayer-heading">
            <div>
              <span>HOBBLI LIVE</span>
              <h2>Online play has entered beta</h2>
            </div>
            <p>Chess and Blackjack both support live Hobbli multiplayer play.</p>
          </div>

          <div className="game-room-multiplayer-grid">
            <article>
              <FaUsers />
              <div>
                <h3>Blackjack Tables</h3>
                <p>Create a private table, join a public table, or share an invite code and play live against the dealer together.</p>
              </div>
              <Link to="/games/blackjack/online"><FaNetworkWired /> Play Online</Link>
            </article>

            <article>
              <FaNetworkWired />
              <div>
                <h3>Chess Tables</h3>
                <p>Create a private table, share the invite code, ready up, and play live move-for-move through Hobbli.</p>
              </div>
              <Link to="/games/chess/online"><FaNetworkWired /> Live Beta</Link>
            </article>
          </div>
        </section>

        <footer className="game-room-arcade-footer">
          <span>MORE GAMES COMING SOON</span>
          <span>HOBBLI GAME ROOM</span>
        </footer>
      </section>
    </div>
  );
}
